import { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'

import {
  FlexV2,
  Box,
  Table,
  Slot,
  Column,
  Row,
  Text,
  Loader
} from '@/primitives'

import { Pagination } from '@/elements'

import HeaderV2 from '../../../Shared/HeaderV2'
import TableFilter from './TableFilter'
import TableRow from './TableRow'

import { requestInventories } from '@/actions/operations/inventory'

import {
  getCurrentUser,
  getOperationsInventories,
  getOperationsInventoriesCount,
  getOperationsLoading
} from '@/reducers/selectors'

import history from '../../../../../history'

import useMediaQuery from '@/hooks/useMediaQuery'

import Strings from '../Strings'

import {
  hasEditPermissions,
  RESOURCE_TYPE_OPERATIONS,
  RESOURCE_TYPE_OPERATIONS_INVENTORY
} from '@/Util/PermissionUtils'

function InventoryTable({ state, dispatchState, modulePath }) {
  const dispatch = useDispatch()

  const currentUser = getCurrentUser()
  const items = getOperationsInventories()
  const totalItems = getOperationsInventoriesCount()
  const loading = getOperationsLoading()

  const isAboveCustomBreakpoint = useMediaQuery('min-width: 620px')

  const [totalPages, setTotalPages] = useState(1)
  const [pageSize, setPageSize] = useState(9)
  const [tableState, setTableState] = useState(state)
  const [canEdit, setCanEdit] = useState(false)

  const strings = Strings()

  useEffect(() => {
    if (currentUser.userName) {
      setCanEdit(
        hasEditPermissions(
          currentUser,
          RESOURCE_TYPE_OPERATIONS,
          RESOURCE_TYPE_OPERATIONS_INVENTORY
        )
      )
    }
  }, [currentUser])

  useEffect(() => {
    if (tableState.page !== state.page || items.length === 0) {
      setTableState({ ...state })
      fetchInventories(state.page)
    }
  }, [state.page])

  useEffect(() => {
    if (
      tableState.organizations !== state.organizations ||
      tableState.storageFacilities !== state.storageFacilities ||
      tableState.produces !== state.produces ||
      tableState.varieties !== state.varieties
    ) {
      setTableState({ ...state })
      fetchInventories(1)
      changePage(1)
    }
  }, [
    state.organizations,
    state.storageFacilities,
    state.produces,
    state.varieties
  ])

  const fetchInventories = page => {
    if (
      state.organizations.length > 0 &&
      state.storageFacilities.length > 0 &&
      state.produces.length > 0 &&
      state.varieties.length > 0
    ) {
      dispatch(
        requestInventories({
          filter: {
            organizationId: state.organizations,
            storageFacilityId: state.storageFacilities,
            produceId: state.produces,
            varietyId: state.varieties
          },
          limit: pageSize,
          offset: page - 1
        })
      )
    }
  }

  const changePage = newPage => {
    dispatchState({
      page: newPage
    })
  }

  const onTransfer = () => {
    history.push(`${modulePath}/transfers`)
  }

  return (
    <FlexV2 axisGap={400} direction='column'>
      <HeaderV2
        title={strings.manageInventory}
        buttonIcon={'sync_alt'}
        buttonText={strings.transfers}
        buttonCallback={onTransfer}
        showButton={canEdit}
        breakpointWidth={900}
      >
        <TableFilter state={state} dispatchState={dispatchState} />
      </HeaderV2>
      <Loader isLoading={loading}>
        {totalItems === 0 && (
          <FlexV2 className={'Operations__Table__Empty'}>
            <Text>{strings.noInventory}</Text>
          </FlexV2>
        )}
        {totalItems > 0 && (
          <Table className='Operations__Table' aria-colcount='4'>
            <Slot name='head'>
              <Row>
                <Column>{strings.storageFacility}</Column>
                <Column>{strings.produce}</Column>
                <Column>{strings.weight}</Column>
                <Column />
              </Row>
            </Slot>
            <Slot name='body'>
              {items?.map(item => (
                <TableRow
                  key={item.id}
                  item={item}
                  dispatchState={dispatchState}
                  modulePath={modulePath}
                />
              ))}
            </Slot>
          </Table>
        )}
        {totalItems > pageSize && (
          <Box className='pagination-menu'>
            <Pagination
              type={isAboveCustomBreakpoint ? 'simple' : 'compact'}
              showPageCount={isAboveCustomBreakpoint}
              totalItems={totalItems}
              totalPages={totalPages}
              setTotalPages={setTotalPages}
              page={tableState.page}
              setPage={changePage}
              pageSize={pageSize}
              setPageSize={setPageSize}
            />
          </Box>
        )}
      </Loader>
    </FlexV2>
  )
}

export default InventoryTable
